import { create } from 'zustand';
import { Api } from 'src/api';

interface FileMetadata {
  id: string;
  name: string;
  uploadId?: string;
  size?: number;
}

interface UploadStatus {
  isUploading: boolean;
  failed?: boolean;
  abortController?: AbortController;
}

interface UploadDetails {
  progress: number;
  remainingTime: number;
  uploadSpeed?: number;
}

interface MediaUploadStore {
  uploads: { [key: string]: FileMetadata };
  details: { [key: string]: UploadDetails };
  status: { [key: string]: UploadStatus };
  /** Cumulative bytes of all completed uploads in this session (for optimistic storage display) */
  totalCompletedBytes: number;
  /** Per-file bytes counted into totalCompletedBytes — used so removeUpload can subtract accurately */
  completedBytesByFile: { [key: string]: number };
  addUpload: (file: FileMetadata) => void;
  isUploading: (fileId: string) => boolean;
  getUpload: (fileId: string) => FileMetadata | undefined;
  getDetails: (fileId: string) => UploadDetails | undefined;
  getStatus: (fileId: string) => UploadStatus | undefined;
  updateProgress: (
    fileId: string,
    progress: number,
    remainingTime: number,
    uploadSpeed?: number,
  ) => void;
  finishUpload: (fileId: string) => void;
  removeUpload: (fileId: string) => void;
  isFailed: (fileId: string) => boolean;
  setFailed: (fileId: string) => void;
  abort: (fileId: string) => void;
  cancelUpload: (fileId: string) => Promise<void>;
}

const useMediaUploadStore = create<MediaUploadStore>((set, get) => ({
  uploads: {},
  details: {},
  status: {},
  totalCompletedBytes: 0,
  completedBytesByFile: {},

  isUploading: (mediaId) => {
    return !!get().status[mediaId]?.isUploading;
  },

  getUpload: (mediaId: string) => {
    return get().uploads[mediaId];
  },

  getDetails: (mediaId: string) => {
    return get().details[mediaId];
  },

  getStatus: (mediaId: string) => {
    return get().status[mediaId];
  },

  isFailed: (mediaId: string) => {
    return !!get().status[mediaId]?.failed;
  },

  addUpload: (file) => {
    set((state) => ({
      uploads: {
        ...state.uploads,
        [file.id]: file,
      },
      status: {
        ...state.status,
        [file.id]: {
          isUploading: true,
          abortController: new AbortController(),
        },
      },
      details: {
        ...state.details,
        [file.id]: {
          progress: 0,
          remainingTime: 0,
          uploadSpeed: 0,
        },
      },
    }));
  },

  updateProgress: (fileId, progress, remainingTime, uploadSpeed) => {
    set((state) => ({
      details: {
        ...state.details,
        [fileId]: { ...state.details[fileId], progress, remainingTime, uploadSpeed },
      },
    }));
  },

  finishUpload: (fileId) => {
    const fileSize = get().uploads[fileId]?.size ?? 0;
    set((state) => {
      // Guard against double-counting if finishUpload is called twice for the same file
      const alreadyCounted = state.completedBytesByFile[fileId] ?? 0;
      const delta = fileSize - alreadyCounted;
      return {
        totalCompletedBytes: state.totalCompletedBytes + delta,
        completedBytesByFile: {
          ...state.completedBytesByFile,
          [fileId]: fileSize,
        },
        details: {
          ...state.details,
          [fileId]: {
            ...state.details[fileId],
            progress: 100,
            remainingTime: 0,
            uploadSpeed: 0,
          },
        },
        status: {
          ...state.status,
          [fileId]: {
            ...state.status[fileId],
            isUploading: false,
          },
        },
      };
    });
  },

  setFailed: (fileId) => {
    set((state) => ({
      status: {
        ...state.status,
        [fileId]: {
          ...state.status[fileId],
          failed: true,
        },
      },
    }));
  },

  removeUpload: (fileId) => {
    set((state) => {
      const uploads = { ...state.uploads };
      delete uploads[fileId];

      const details = { ...state.details };
      delete details[fileId];

      const status = { ...state.status };
      delete status[fileId];

      // Subtract this file's previously-counted bytes so the cumulative total tracks
      // deletions, not just uploads. Floor at 0 to stay resilient against double-removes.
      const countedBytes = state.completedBytesByFile[fileId] ?? 0;
      const completedBytesByFile = { ...state.completedBytesByFile };
      delete completedBytesByFile[fileId];

      return {
        uploads,
        details,
        status,
        completedBytesByFile,
        totalCompletedBytes: Math.max(0, state.totalCompletedBytes - countedBytes),
      };
    });
  },

  abort: (fileId) => {
    get().status[fileId]?.abortController?.abort();
  },

  cancelUpload: async (fileId) => {
    const upload = get().uploads[fileId];
    
    try {
      // First abort the local upload process
      get().status[fileId]?.abortController?.abort();
      
      // Then call the API to cancel the upload on the server
      if (upload?.uploadId) {
        await Api.media.cancelUpload(upload.uploadId);
      }
      
      // Remove the upload from the store after successful cancellation
      get().removeUpload(fileId);
    } catch (error) {
      console.error('Failed to cancel upload:', error);
      // Mark as failed if API call fails but still abort locally
      get().setFailed(fileId);
    }
  },
}));

export { useMediaUploadStore };
export type { FileMetadata, UploadDetails, UploadStatus };
